import { useMutation } from "@apollo/client";
import { Editor } from "@tiptap/react";
import { compact } from "lodash";
import { useContext, useState } from "react";
import {
  CreateAskAiMessageMutationMutation,
  CreateAskAiMessageMutationMutationVariables,
  GetAskAiChatSessionQueryQuery,
  GetAskAiChatSessionQueryQueryVariables,
} from "types/graphql-schema";

import cache from "@cache/cache";
import Button, { ButtonSize, buttonTheme } from "@components/button/button";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { pluralize, sanitizeMarkdown } from "@helpers/string";

import { ChatSessionContext } from "../ask-ai";
import createAskAiMessageMutation from "../graphql/create-ask-ai-message-mutation";
import getAskAiSessionQuery from "../graphql/get-ask-ai-session-query";
import { createChatMessageNode } from "../helpers";
import AskAIMessageMarkdown from "./ask-ai-message-markdown";
import { SuggestedAssessmentResponsesPlaceholderType } from "./ask-ai-placeholders";
import AskAICitationModal from "./citations-modal";

const AskAIEmbedSuggestedAssessmentResponses = ({
  suggestedAssessmentResponses,
  canBeEdited = false,
  chatSessionId,
}: {
  suggestedAssessmentResponses: SuggestedAssessmentResponsesPlaceholderType["responses"];
  canBeEdited?: boolean;
  chatSessionId?: number;
}) => {
  const [showCitationsModal, setShowCitationsModal] = useState(false);
  const responses = compact(suggestedAssessmentResponses);
  const [index, setIndex] = useState(0);
  const displayedResponse = responses[index];
  const citations = compact(displayedResponse?.citations || []);
  const { assessmentQuestionId } = useContext(ChatSessionContext);

  const [createAskAIMessage, { loading: loadingAddMessage }] = useMutation<
    CreateAskAiMessageMutationMutation,
    CreateAskAiMessageMutationMutationVariables
  >(createAskAiMessageMutation);

  const handleCreateAskAIChatSession = () => {
    if (loadingAddMessage || !chatSessionId) return;
    const message = `Restart working from this response:\n\n${
      displayedResponse?.suggestedResponse || ""
    }`;
    const cachedChatSession = cache.readQuery<
      GetAskAiChatSessionQueryQuery,
      GetAskAiChatSessionQueryQueryVariables
    >({
      query: getAskAiSessionQuery,
      variables: { chatSessionId },
    });
    createAskAIMessage({
      variables: { chatSessionId, message },
      optimisticResponse: cachedChatSession?.chatSession
        ? {
            __typename: "Mutation",
            sendMessageToChatbot: {
              __typename: "SendMessageToChatbotMutation",
              chatSession: {
                ...cachedChatSession?.chatSession,
                updated: new Date().toISOString(),
                messages: {
                  __typename: "ChatMessageConnection",
                  edges: [
                    ...cachedChatSession?.chatSession.messages.edges,
                    {
                      __typename: "ChatMessageEdge",
                      node: createChatMessageNode({ content: message }),
                    },
                  ],
                },
              },
            },
          }
        : undefined,
    });
  };

  const handleClickEdit = () => {
    handleCreateAskAIChatSession();
  };

  const handleClickInsertResponse = () => {
    const suggestedResponse = displayedResponse?.suggestedResponse || "";
    if (!assessmentQuestionId || !suggestedResponse) return;
    const element = document.querySelector(
      `#assessment-question-${assessmentQuestionId} .ProseMirror`
    ) as HTMLDivElement & { editor: Editor };
    if (element && element.editor) {
      // insert content at the end of the editor and focus on it
      const contentToInsert = `${
        element.editor.isEmpty ? "" : "\n\n"
      }${suggestedResponse}`;
      element.editor
        .chain()
        .focus("end")
        .insertContent(contentToInsert)
        .focus()
        .run();
    }
  };

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="flex items-center justify-between gap-4">
        <div className="font-medium text-sm text-gray-700">
          Suggested Responses
        </div>
        {responses.length > 1 && (
          <div className="flex items-center gap-2">
            <Button
              icon={getTopicflowIcon("arrowLeft")}
              theme={buttonTheme.iconGray}
              size={ButtonSize.small}
              onClick={() => setIndex(index - 1)}
              disabled={index === 0}
            />
            <span className="text-gray-500">
              {index + 1}/{responses.length}
            </span>
            <Button
              icon={getTopicflowIcon("arrowRight")}
              theme={buttonTheme.iconGray}
              size={ButtonSize.small}
              onClick={() => setIndex(index + 1)}
              disabled={index === responses.length - 1}
            />
          </div>
        )}
      </div>
      <div className="border rounded-lg py-4 px-4 flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <div>
            <button
              className="text-gray-500"
              onClick={() => setShowCitationsModal(true)}
              disabled={citations.length === 0}
            >
              {`${citations.length} ${pluralize(
                "source",
                citations.length
              )} used`}
            </button>
          </div>
          <div className="flex items-center gap-2">
            {canBeEdited && chatSessionId && (
              <Button
                text="Edit"
                size={ButtonSize.medium}
                onClick={handleClickEdit}
                disabled={loadingAddMessage}
              />
            )}
            <Button
              text="Insert Response"
              theme={buttonTheme.lightBlue}
              size={ButtonSize.medium}
              onClick={handleClickInsertResponse}
            />
          </div>
        </div>
        <div className="prose prose-sm max-w-none">
          <AskAIMessageMarkdown>
            {sanitizeMarkdown(displayedResponse?.suggestedResponse || "")}
          </AskAIMessageMarkdown>
        </div>
      </div>
      {showCitationsModal && (
        <AskAICitationModal
          citations={citations}
          onClose={() => setShowCitationsModal(false)}
        />
      )}
    </div>
  );
};

export default AskAIEmbedSuggestedAssessmentResponses;
